import {
  BufferGeometry,
  Float32BufferAttribute,
  Material,
  Object3D,
  Points,
  ShaderMaterial,
  Vector3,
} from 'three';

import fragmentShader from '../../../../../static/glsl/volume/volume.frag';
import vertexShader from '../../../../../static/glsl/volume/volume.vert';
import gradientFragmentShader from '../../../../../static/glsl/volume/volume-gradient.frag';
import gradientTransparentFragmentShader from '../../../../../static/glsl/volume/volume-gradient-transparent.frag';
import { WebApi } from '../../../../services/web-api';
import { getMaterials } from '../../../../utils/three/material';
import { isDefined } from '../../../../utils/variables';
import { CoordinateManager } from '../../../coordinate-manager';
import { clamp, toVector3 } from '../../../utilities/math-helpers';

interface VolumeModelColorSetup {
  base: number[];
  top: number[];
}

interface VolumeModelData {
  vertices: Float32Array;
  slopes: Float32Array;
  heights?: Float32Array;
}

/**
 *  Colors used by the slope-based (non-gradient) volume shader.
 */
export const volumeColors = {
  fill: {
    base: [0, 0.08, 0.25],
    top: [0, 0.45, 0.82],
  } as VolumeModelColorSetup,
  cut: {
    base: [0.32, 0.1, 0.07],
    top: [1, 0.25, 0.18],
  } as VolumeModelColorSetup,
};

/**
 *  The cut/fill gradient palette. Single source of truth shared between the
 *  viewer's gradient shader (via the `u_palette` uniform) and the CSS color bar
 *  legend, so the rendered points always match the legend.
 *
 *  Ordered from the innermost band (smallest |height|, index 0) to the
 *  outermost band (largest |height|, index 4), matching the four
 *  `opacity_threshold_table` entries.
 */
export const cutColorBarPalette = ['#fff7e6', '#ffd4c2', '#ff9980', '#ff6047', '#d60e00'];
export const fillColorBarPalette = ['#dbfff9', '#b3ffff', '#66e6ff', '#1ab3ff', '#0066cc'];

function hexToVector3(hex: string): Vector3 {
  const value = parseInt(hex.slice(1), 16);
  return new Vector3(((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255);
}

/**
 *  Builds and manages the volume model.
 */
export class CellGridVolumeModel extends Object3D {
  projectPosition = new Vector3(0, 0, 0);
  private _opacity = 1;
  _displayGradient: boolean = false;

  /**
   *  Setter and getters for the fill volume model
   */
  get fillVisibleState() {
    return this.fillModel.visible;
  }

  set fillVisibleState(state: boolean) {
    this.fillModel.visible = state;
  }

  get opacity() {
    return this._opacity;
  }

  set opacity(value: number) {
    this._opacity = clamp(value, 0, 1);

    for (const geometry of [...this.cutModel.children, ...this.fillModel.children]) {
      for (const material of getMaterials(geometry)) {
        material.transparent = this._opacity < 1;
        (material as ShaderMaterial).uniforms.base_opacity.value = this._opacity;
      }
    }

    this.refreshMaterial();
  }

  /**
   *  Setter and getters for the cut volume model
   */
  get cutVisibleState() {
    return this.cutModel.visible;
  }

  set cutVisibleState(state: boolean) {
    this.cutModel.visible = state;
  }

  get planarAreaCut() {
    return this._planarAreaCut;
  }

  get planarAreaFill() {
    return this._planarAreaFill;
  }

  /**
   *  Setter and getter for the models raised state.
   */
  set raised(state: boolean) {
    this.isRaised = state;
    this.cutModel.position.set(0, 0, this.isRaised ? this.raisedHeight : 0);
    this.fillModel.position.set(0, 0, this.isRaised ? this.raisedHeight : 0);
  }

  get raised() {
    return this.isRaised;
  }

  /** How far the model is raised, in world units. */
  get raise() {
    return this.isRaised ? this.raisedHeight : 0;
  }

  get displayGradient() {
    return this._displayGradient;
  }

  set displayGradient(state: boolean) {
    this._displayGradient = state;
    if (!this.containsHeightData) {
      return;
    }

    this.refreshMaterial();
  }

  private refreshMaterial() {
    const newFragmentShader =
      this.displayGradient && this.opacity < 1
        ? gradientTransparentFragmentShader
        : this.displayGradient
          ? gradientFragmentShader
          : fragmentShader;

    for (const cutPoints of this.cutModel.children) {
      const shaderMaterial = (cutPoints as Points).material as ShaderMaterial;
      shaderMaterial.fragmentShader = newFragmentShader;
      shaderMaterial.uniforms.opacity_threshold_table.value = this.cut_opacity_threshold_table;
      shaderMaterial.transparent = this.opacity < 1;
      shaderMaterial.needsUpdate = true;
    }

    for (const fillPoints of this.fillModel.children) {
      const shaderMaterial = (fillPoints as Points).material as ShaderMaterial;
      shaderMaterial.fragmentShader = newFragmentShader;
      shaderMaterial.uniforms.opacity_threshold_table.value = this.fill_opacity_threshold_table;
      shaderMaterial.transparent = this.opacity < 1;
      shaderMaterial.needsUpdate = true;
    }
  }

  /**
   * Fetches and parses the cut and fill volume model data from the specified url
   * @param url
   */
  static async fetchVolumeChunk(url: string): Promise<{
    cutData: VolumeModelData;
    fillData: VolumeModelData;
    containsHeightData: boolean;
  }> {
    const modelArrayBuffer = await WebApi.fetchS3DataAsArrayBuffer(url);

    const headerLength = 2;
    const header = new Float32Array(modelArrayBuffer, 0, headerLength);
    const byteSize = 4;
    const headerCount = 2;
    const cutCount = header[0];
    const fillCount = header[1];
    const containsHeightData =
      modelArrayBuffer.byteLength / byteSize > cutCount * 4 + fillCount * 4 + headerCount;

    const cutData = CellGridVolumeModel.extractModelData(
      modelArrayBuffer,
      headerCount,
      cutCount,
      containsHeightData,
    );
    const fillData = CellGridVolumeModel.extractModelData(
      modelArrayBuffer,
      headerCount + cutCount * (containsHeightData ? 5 : 4),
      fillCount,
      containsHeightData,
    );

    return {
      cutData,
      fillData,
      containsHeightData,
    };
  }

  /**
   * Same behaviour as "calculateLocalPosition" in ElementObj.
   */
  refreshLocalPosition() {
    this.position.copy(
      toVector3(
        CoordinateManager.getInstance().projectToLocal(
          this.projectPosition.x,
          this.projectPosition.y,
        ),
        this.projectPosition.z - CoordinateManager.getInstance().getMAS(),
      ),
    );
  }

  private static extractModelData(
    arrayBuffer: ArrayBuffer,
    offset: number,
    vertexCount: number,
    containsHeightData: boolean,
  ): VolumeModelData {
    const vertices = new Float32Array(arrayBuffer, offset * 4, vertexCount * 3);
    offset += vertexCount * 3;
    const slopes = new Float32Array(arrayBuffer, offset * 4, vertexCount);
    offset += vertexCount;

    let heights;
    if (containsHeightData) {
      heights = new Float32Array(arrayBuffer, offset * 4, vertexCount);
      offset += vertexCount;
    }

    return {
      vertices,
      slopes,
      heights,
    };
  }

  containsHeightData = false;
  cut_opacity_threshold_table: number[] = [];
  fill_opacity_threshold_table: number[] = [];

  private cellArea: number;
  private _planarAreaCut: number[] = [];
  private _planarAreaFill: number[] = [];
  private raisedHeight = 7.5;
  private isRaised: boolean;

  min = 0;
  max = 0;

  private cutModel: Object3D = new Object3D();
  private fillModel: Object3D = new Object3D();

  constructor(
    readonly volumeId: string,
    cellWidth?: number,
    cellDepth?: number,
  ) {
    super();
    this.add(this.cutModel, this.fillModel);

    // If the cellSize is not available, assume a cellsize of 10x10 cm
    this.cellArea = cellWidth && cellDepth ? cellWidth * cellDepth : 0.01;

    this.isRaised = true;
    this.raised = true;
  }

  /**
   * Returns a positive value indicating the height difference between two cut sections.
   */
  calcIntervalCut(customInterval?: number) {
    const max = isDefined(customInterval) ? Math.abs(customInterval) * 5 : this.max;
    return max / 5;
  }

  /**
   * Returns a negative value indicating the height difference between two fill sections.
   */
  calcIntervalFill(customInterval?: number) {
    const min = isDefined(customInterval) ? -Math.abs(customInterval) * 5 : this.min;
    return min / 5;
  }

  resetIntervalCut(customInterval?: number) {
    const segment = this.calcIntervalCut(customInterval);
    this.cut_opacity_threshold_table = [segment * 1, segment * 2, segment * 3, segment * 4];
    this.refreshMaterial();
  }

  resetIntervalFill(customInterval?: number) {
    const segment = this.calcIntervalFill(customInterval);
    this.fill_opacity_threshold_table = [segment * 1, segment * 2, segment * 3, segment * 4];
    this.refreshMaterial();
  }

  // Builds the model based on the s3 url
  async loadVolumeModel(urls: string[]) {
    for (const url of urls) {
      const volumeData = await CellGridVolumeModel.fetchVolumeChunk(url);
      this.containsHeightData = volumeData.containsHeightData;

      this.cutModel.add(this.buildVolumePart(volumeData.cutData, false));
      this.fillModel.add(this.buildVolumePart(volumeData.fillData, true));
    }

    this.resetIntervalCut();
    this.resetIntervalFill();
    this.calculatePlanarAreaCut();
    this.calculatePlanarAreaFill();
  }

  dispose() {
    for (const cutPoints of this.cutModel.children) {
      (cutPoints as Points).geometry.dispose();
      ((cutPoints as Points).material as Material).dispose();
    }

    for (const fillPoints of this.fillModel.children) {
      (fillPoints as Points).geometry.dispose();
      ((fillPoints as Points).material as Material).dispose();
    }
  }

  /**
   * Calculates the total planar area for cut and returns the value in m2 for each cut treshold
   */
  calculatePlanarAreaCut() {
    this._planarAreaCut = this.calculatePlanarArea(
      this.cutModel.children,
      this.cut_opacity_threshold_table,
    );
  }

  /**
   * Calculates the total planar area for fill and returns the value in m2 for each fill treshold
   */
  calculatePlanarAreaFill() {
    this._planarAreaFill = this.calculatePlanarArea(
      this.fillModel.children,
      this.fill_opacity_threshold_table,
    );
  }

  /**
   *  Extracts the array buffer into the different typed arrays of the ThreeJs buffer; position & intensity.
   *  Intensity is used to allow the client to define the color and give a gradient volume coloring.
   * @param modelData
   * @param fill
   * @param modelData
   * @param fill
   */
  private buildVolumePart(modelData: VolumeModelData, fill: boolean) {
    const bufferGeometry = new BufferGeometry();
    bufferGeometry.setAttribute('position', new Float32BufferAttribute(modelData.vertices, 3));
    bufferGeometry.setAttribute('slope', new Float32BufferAttribute(modelData.slopes, 1, true));

    if (modelData.heights) {
      bufferGeometry.setAttribute('height', new Float32BufferAttribute(modelData.heights, 1, true));

      if (fill) {
        for (const height of bufferGeometry.getAttribute('height').array) {
          this.min = height < this.min ? height : this.min;
        }
      } else {
        for (const height of bufferGeometry.getAttribute('height').array) {
          this.max = height > this.max ? height : this.max;
        }
      }
    }

    bufferGeometry.computeBoundingSphere();

    const colorSetup = fill ? volumeColors.fill : volumeColors.cut;
    const palette = (fill ? fillColorBarPalette : cutColorBarPalette).map(hexToVector3);
    const uniforms = {
      u_colors: { value: [new Vector3(...colorSetup.base), new Vector3(...colorSetup.top)] },
      u_palette: { value: palette },
      opacity_threshold_table: {
        value: [0.2, 0.4, 0.6, 0.8],
      },
      base_opacity: { value: 1.0 },
      display_gradient: { value: false },
    };

    // TODO: Implement dynamic loading of WebGL files as imports to refactor the code into separate files
    // Also TODO: Refactor render effects to better define the volume visuals.
    return new Points(
      bufferGeometry,
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        vertexColors: true,
        uniforms,
      }),
    );
  }

  private calculatePlanarArea(chunks: Object3D[], tresholdTable: number[]) {
    if (!this.containsHeightData) {
      return [];
    }

    const planarArea = [0, 0, 0, 0, 0];

    for (const chunk of chunks) {
      if (
        chunk instanceof Points &&
        chunk.geometry instanceof BufferGeometry &&
        chunk.geometry.attributes.height?.array !== undefined
      ) {
        for (const height of chunk.geometry.attributes.height.array as number[]) {
          for (let segment = 0; segment < 5; segment++) {
            if (Math.abs(height) < Math.abs(tresholdTable[segment]) || segment === 4) {
              planarArea[segment] += this.cellArea;
              break;
            }
          }
        }
      }
    }

    return planarArea;
  }
}
